import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAuthStore } from '@/stores/auth-store'
import { API_BASE } from '@/lib/config'
import { currentLocale } from '@/lib/i18n'
import { useLatestReleaseNote } from '@/hooks/use-release-note'
import {
  RELEASE_NOTES, markDismissed, markShown, pickNoteToShow, readStore, releaseNotePath, writeStore, type ReleaseNote,
} from '@/lib/release-notes'

/** Thông báo cập nhật: video animation (HTML) + các nhóm tính năng mới. Quy tắc hiện/tắt xem lib/release-notes.ts. */
export function ReleaseNoteDialog() {
  const { t } = useTranslation('sharedMisc')
  const userId = useAuthStore((s) => s.user?.id)
  const [note, setNote] = useState<ReleaseNote | null>(null)
  const checked = useRef(false)
  const latest = useLatestReleaseNote(!!userId)

  useEffect(() => {
    if (!userId || checked.current || latest.isLoading) return
    checked.current = true
    const store = readStore()
    const n = latest.data?.note
    // Nguồn chính: bản Platform đang bật; chưa có thì dùng bản dự phòng trong mã nguồn.
    const list: ReleaseNote[] = n
      ? [{ id: n.slug, date: new Date(n.releasedAt).toLocaleDateString(currentLocale()), title: n.title, highlights: n.highlights, maxShows: n.maxShows, htmlUrl: `${API_BASE}/release-notes/${n.slug}/html` }]
      : RELEASE_NOTES
    const pick = pickNoteToShow(list, store)
    if (!pick) return
    writeStore(markShown(store, pick.id)) // đếm 1 lần cho mỗi lần mở trang
    setNote(pick)
  }, [userId, latest.isLoading, latest.data])

  if (!note) return null
  const close = () => setNote(null)
  const never = () => {
    writeStore(markDismissed(readStore(), note.id))
    close()
  }
  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> {note.title}</DialogTitle>
          <DialogDescription>{t('releaseNoteDialog.updatedOn', { date: note.date })}</DialogDescription>
        </DialogHeader>
        <iframe
          title={note.title}
          src={releaseNotePath(note)}
          sandbox="allow-scripts allow-same-origin"
          className="aspect-video w-full rounded-lg border bg-slate-900"
        />
        <ul className="list-disc space-y-1 pl-5 text-sm">
          {note.highlights.map((h) => <li key={h}>{h}</li>)}
        </ul>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={never}>{t('releaseNoteDialog.neverShowAgain')}</Button>
          <Button onClick={close}>{t('releaseNoteDialog.gotIt')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
